import DateFnsUtils from "@date-io/date-fns";
import {
    Container,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Link,
    FormControlLabel,
    Checkbox,
} from "@material-ui/core";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from 'react-i18next';
import {
    PrimaryButton as ButtonPrimary,
    CircularProggressIndicator,
    KeyboardDatePickerInput,
} from "../components-atoms";
import {
    CollectionOrderCounterInput,
    CollectionOrderInput,
} from "../components-atoms/CollectionOrderInput";
import { InfoSectionContainer } from "../components-molecules/InfoSectionContainer";
import {
    CONTACT_PHONE,
    VehicleTypeSelector,
} from "../components-molecules/VehicleTypeSelector";
import { CollectionOrderHeader } from "../components-organisms/CollectionOrderHeader";
import { NewCollectionOrderTotal } from "../components-organisms/NewCollectionOrderTotal";
import { EMAIL_VALIDATION } from "../constants/validation";
import {
    CreateCollectOrder,
    getCollectionContactDetails,
    getQuote,
    getShopAddress,
    placeCollectOrder,
    validatePhoneNumber,
    ShopAddressResponse,
    IGetCollectionContactDetailsModel,
} from "../redux/actions/collectOrders";
import { newCollectionStyles } from "../utils/styles/newCollectionStyles";
import packageJson from "../../package.json";
import { AcceptTermsModal, ErrorModal } from "../components-molecules";
import { GooglePlaceInput } from "../components-atoms/GooglePlaceInput";
import { IGooglePlaceResponse } from "../utils/googlePlacesUtil";
import { useDispatch, useSelector } from "react-redux";
import { ConfirmModal } from "../components-molecules/ConfirmModal";
import { theme } from "../constants/theme";
import { useDidMountEffect } from "../utils/useDidMount";
import {
    clearCollectionTime,
    getCollectionTime,
} from "../redux/actions/getCollectionTime";
import moment from "moment";
import enLocale from "date-fns/locale/en-GB";
import { prepareAddressBody } from "../utils/addressStringConvertor";
import {
    CollectionOrderError,
    DEFAULT_VALIDATION_MSG,
    IAvailableSlots,
    ITimeSlot,
    ORKESTRO_ERROR,
    REF_NUMBER_LENGTH,
    hasValidAddress,
    mapShopAddressToInputs,
} from "../utils/collectOrderConstants";
import { getMultipleValuesAsText } from "../utils/getMultipleValuesAsText";
import { DeliveryTypeSelector } from "../components-molecules/DeliveryTypeSelector";
import {
    GetCollectionOrderDetailRequest,
    ICollectOrderDetailResponseModel,
} from "../redux/actions/collectionOrderDetail";
import { prepareFormBody } from "../utils/getCollectFormBody";
import { getKeyboardPickerDateFormat, getTimeFormat } from "../utils/dateTimeFormatting";
// import { mockData } from "../utils/collectOrderMockData";
// import config from "../config";

export function NewCollectionOrder() {
    const { t } = useTranslation();
    const isTkPortal = packageJson.name === "tk.portal";
    const userData = useSelector((state: any) => state.login);
    const role = useSelector((state: any) => state.login.role);
    const [collectionTimeData, collectionTimeLoading]: [
        Array<ITimeSlot>,
        boolean
    ] = useSelector((state: any) => [
        state.getCollectionTime.data,
        state.getCollectionTime.loading,
    ]);

    // Repeat booking feature
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const repeatBookingId = urlParams.get("repeatBookingId");
    const [detailRequestLoading, setDetailRequestLoading] = useState(false);

    const dispatch = useDispatch();
    const classes = newCollectionStyles();
    const [orderId, setOrderId] = useState<string>();
    const [lastUpdate, setLastUpdate] = useState(new Date());

    const [getPriceLoading, setGetPriceLoading] = useState<boolean>(false);
    const [quoteIds, setQuoteIds] = useState([]);

    const [placeOrderLoading, setPlaceOrderLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [quoteError, setQuoteError] = useState("");
    const [orkestroError, setOrkestroError] = useState("");

    // T&C modal
    const [TCvisible, setTCvisible] = useState<boolean>(false);
    const [TCaccepted, setTCAccepted] = useState<boolean>(
        userData.termsAccepted
    );

    // Is Karter popup canceled
    const [showKarterPopup, setShowKarterPopup] = useState<boolean>(false);
    const karterPopupRef = useRef(showKarterPopup);
    karterPopupRef.current = showKarterPopup;

    // Address input validations
    const [collectionAddressError, setCollectionAddressError] =
        useState<string>();
    const [deliveryAddressError, setDeliveryAddressError] = useState<string>();
    const [maxPriceQuoteId, setMaxPriceQuoteId] = useState<string | null>(null);

    // Phone number validation states
    const [pickupPhoneError, setPickupPhoneError] = useState<string>();
    const [deliveryPhoneError, setDeliveryPhoneError] = useState<string>();
    const [isPickupPhoneValid, setIsPickupPhoneValid] = useState(false);
    const [isDeliveryPhoneValid, setIsDeliveryPhoneValid] = useState(false);

    // Admin can not create a collect order
    if (!userData.isCollectEnabled || role === "Admin") {
        window.location.href = "/";
    }

    const {
        control,
        formState: { isValid, errors },
        setValue,
        getValues,
        handleSubmit,
        watch,
        trigger,
    } = useForm({
        mode: "all",
        // defaultValues: config.env === 'dev' ? mockData : {}
    });

    const body = getValues();
    const hasAddressError =
        !(
            collectionAddressError === "" ||
            collectionAddressError === undefined
        ) ||
        !(deliveryAddressError === "" || deliveryAddressError === undefined) ||
        !hasValidAddress(body.CollectionAddress) ||
        !hasValidAddress(body.DeliveryAddress) ||
        !isPickupPhoneValid ||
        !isDeliveryPhoneValid ||
        Boolean(pickupPhoneError) ||
        Boolean(deliveryPhoneError);

    const getLastTimeSlotForSelectedDay = useCallback((selectedDate: Date) => {
        if (!Array.isArray(collectionTimeData)) {
            return null;
        }

        const slotsForSelectedDay = collectionTimeData.filter(
            (slot) =>
                new Date(slot.Start).getDate() ===
                new Date(selectedDate).getDate()
        );

        if (slotsForSelectedDay.length > 0) {
            return slotsForSelectedDay[slotsForSelectedDay.length - 1];
        }

        return null;
    }, [collectionTimeData]);

    const localDateTime = moment.utc(body.PickUpTimeSlot).local();
    const flexibleCond = useMemo(() => {
        const lastTimeSlot = getLastTimeSlotForSelectedDay(body.PickUpTimeSlot);
        if (lastTimeSlot) {
            const flexibleCutoff = moment
                .utc(lastTimeSlot.Start)
                .subtract(lastTimeSlot.FlexibleCutoffThreshold, "hours");
            return (
                body.DeliveryTypeKey === "flexible" &&
                localDateTime.isAfter(flexibleCutoff)
            );
        }
        return false;
    }, [body.PickUpTimeSlot, body.DeliveryTypeKey, getLastTimeSlotForSelectedDay, localDateTime]);

    const flexibleError = useMemo(() => {
        const lastTimeSlot = getLastTimeSlotForSelectedDay(body.PickUpTimeSlot);
        if (lastTimeSlot) {
            const flexibleCutoff = moment
                .utc(lastTimeSlot.Start)
                .subtract(lastTimeSlot.FlexibleCutoffThreshold, "hours");
            return `${t('newCollectionOrder.flexibleDeliveryUnavailable')} ${flexibleCutoff.format(
                "h:mm A"
            )}.`;
        }
        return "";
}, [body.PickUpTimeSlot, getLastTimeSlotForSelectedDay, t]);

    // Get shop address
    useEffect(() => {
        if (isTkPortal) {
            getShopAddress(
                (res: ShopAddressResponse) => {
                    onCollectionAddressChange(mapShopAddressToInputs(res));
                },
                (err) => {
                    console.error("Error:", err);
                }
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const validatePhoneNumberField = useCallback(
        (phoneNumber: string, type: 'pickup' | 'delivery') => {
            if (!phoneNumber || phoneNumber.trim() === '') {
                if (type === 'pickup') {
                    setPickupPhoneError(undefined);
                    setIsPickupPhoneValid(false);
                } else {
                    setDeliveryPhoneError(undefined);
                    setIsDeliveryPhoneValid(false);
                }
                return;
            }


            validatePhoneNumber(
                phoneNumber,
                (response) => {
                    if (response.Valid) {
                        if (type === 'pickup') {
                            setPickupPhoneError(undefined);
                            setIsPickupPhoneValid(true);
                            // Update with formatted phone number
                            setValue("PickupPhone", response.PhoneNumber);
                        } else {
                            setDeliveryPhoneError(undefined);
                            setIsDeliveryPhoneValid(true);
                            // Update with formatted phone number
                            setValue("TelNo", response.PhoneNumber);
                        }
                    } else {
                        if (type === 'pickup') {
                            setPickupPhoneError("Invalid phone number");
                            setIsPickupPhoneValid(false);
                        } else {
                            setDeliveryPhoneError("Invalid phone number");
                            setIsDeliveryPhoneValid(false);
                        }
                    }
                },
                (error) => {
                    if (type === 'pickup') {
                        setPickupPhoneError(error);
                        setIsPickupPhoneValid(false);
                    } else {
                        setDeliveryPhoneError(error);
                        setIsDeliveryPhoneValid(false);
                    }
                }
            );
        },
        [setValue]
    );

    useEffect(() => {
        if (repeatBookingId) {
            setDetailRequestLoading(true);
            GetCollectionOrderDetailRequest(
                repeatBookingId,
                (res: ICollectOrderDetailResponseModel) => {
                    const body = prepareFormBody(res);
                    Object.entries(body).forEach(([name, value]) =>
                        setValue(name, value)
                    );

                    validatePhoneNumberField(body.TelNo, 'delivery');
                    validatePhoneNumberField(body.PickupPhone, 'pickup');
                    setDetailRequestLoading(false);
                },
                (err) => {
                    setDetailRequestLoading(false);
                }
            );
        }
    }, [repeatBookingId, setValue, validatePhoneNumberField]);

    const getRemainingSlots = (selectedDate: Date) => {
        if (collectionTimeData.length > 0) {
            const result = collectionTimeData.filter(
                (slot) =>
                    new Date(slot.Start).getDate() ===
                    new Date(selectedDate).getDate()
            );
            if (result.length > 0) {
                let data: IAvailableSlots = {
                    day: result[0].Start,
                    availableSlots: [],
                };

                result.forEach((slot) => {
                    const label = moment
                        .utc(slot.Start)
                        .local()
                        .format(getTimeFormat());
                    const newSlot = {
                        value: slot.Start,
                        label: label,
                    };
                    data.availableSlots.push(newSlot);
                });
                return data;
            }
        }

        return null;
    };

    const pickupOptions = useMemo(() => {
        const currentDate = new Date();
        const data = getRemainingSlots(body.PickUpTime || currentDate);

        if (data && body.PickUpTime === undefined) {
            setValue("PickUpTime", collectionTimeData[0].Start);
        } else if (
            collectionTimeData.length > 0 &&
            body.PickUpTime === undefined
        ) {
            setValue("PickUpTime", collectionTimeData[0].Start);
        }
        if (!data) {
            return;
        }

        if (!body.PickUpTime) {
            setValue("PickUpTime", data.day);
        }
        if (data.availableSlots.length > 0) {
            setValue("PickUpTimeSlot", data.availableSlots[0].value);
        } else {
            setValue("PickUpTimeSlot", "");
        }
        return data;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [body.PickUpTime, collectionTimeData]);

    const getCollectionContactDetailsRequest = () => {
        getCollectionContactDetails(
            (res: IGetCollectionContactDetailsModel) => {
                setValue("PickupFirstName", res.FirstName);
                trigger("PickupFirstName");

                setValue("PickupLastName", res.LastName);
                trigger("PickupLastName");

                setValue("PickupPhone", res.Phone);
                trigger("PickupPhone");

                validatePhoneNumberField(res.Phone, 'pickup');
            },
            () => {}
        );
    };

    useEffect(() => {
        if (repeatBookingId === null) {
            getCollectionContactDetailsRequest();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const handleFormChange = () => {
        setLastUpdate(new Date());
        setOrderId(undefined);
        setGetPriceLoading(false);
        setQuoteIds([]);
    };

    useEffect(() => {
        const subscription = watch(() => {
            handleFormChange();
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    useEffect(() => {
        dispatch(getCollectionTime());
        return () => {
            dispatch(clearCollectionTime());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function checkFormErrors() {
        // eslint-disable-next-line func-names
        return new Promise(function (resolve) {
            const hasCollectionAddress = hasValidAddress(
                body.CollectionAddress
            );
            const hasDeliveryAddress = hasValidAddress(body.DeliveryAddress);
            const hasCollectionError = !hasCollectionAddress;
            const hasDeliveryError = !hasDeliveryAddress;

            setCollectionAddressError(
                hasCollectionError ? DEFAULT_VALIDATION_MSG : undefined
            );

            setDeliveryAddressError(
                hasDeliveryError ? DEFAULT_VALIDATION_MSG : undefined
            );

            // Validate phone numbers if they haven't been validated yet
            if (body.PickupPhone && !isPickupPhoneValid) {
                validatePhoneNumberField(body.PickupPhone, 'pickup');
            }
            if (body.TelNo && !isDeliveryPhoneValid) {
                validatePhoneNumberField(body.TelNo, 'delivery');
            }

            // Reject promise
            if (hasCollectionError || hasDeliveryError || !isPickupPhoneValid || !isDeliveryPhoneValid) {
                resolve(true);
            }

            resolve(false);
        });
    }

    const handlePlaceOrder = async (id?: string) => {
        if (!TCaccepted) {
            setTCvisible(true);
            return;
        }

        const hasAddressError = await checkFormErrors();
        if (!isValid || hasAddressError) {
            return;
        }

        if (!orderId) {
            setModalOpen(true);
            return;
        }

        setPlaceOrderLoading(true);
        placeCollectOrder(
            {
                Id: orderId,
                SelectedQuoteIds: id ? [id] : quoteIds,
                MaxPriceQuoteId: maxPriceQuoteId,
            },
            () => {
                setShowKarterPopup(true);
            },
            () => {
                setOrkestroError(ORKESTRO_ERROR.PlaceOrder);
                setPlaceOrderLoading(false);
            }
        );
    };

    useDidMountEffect(() => {
        if (showKarterPopup && orderId) {
            window.location.href = `/delivery-order/${orderId}`;
        }
    }, [showKarterPopup]);

    const onCollectionAddressChange = (e: IGooglePlaceResponse) => {
        const CollectionAddress = prepareAddressBody(e);
        setValue("CollectionAddress", CollectionAddress);
        handleFormChange();
    };

    const onDeliveryAddressChange = (e: IGooglePlaceResponse) => {
        const DeliveryAddress = prepareAddressBody(e);
        setValue("DeliveryAddress", DeliveryAddress);
        handleFormChange();
    };

    const handleError = (err) => {
        setOrderId(undefined);
        if (
            err &&
            err === CollectionOrderError.NoFleetFoundForGivenVehicleType
        ) {
            setQuoteError(err);
        } else {
            setOrkestroError(ORKESTRO_ERROR.Price);
        }
    };

    const onGetPrice = async () => {
        const hasAddressError = await checkFormErrors();
        if (!isValid || hasAddressError || flexibleCond) {
            return;
        }

        // Validation passed, set loading
        setGetPriceLoading(true);

        const restriction =
            t('newCollectionOrder.checkIdRestriction');
        const puInstructions = [
            body.PickUpInstructions,
        ];
        const cInstructions = [
            body.CustomerInstructions,
        ];
        if (body.HasRestrictedItems) {
            cInstructions.unshift(restriction);
        }

        // Create a copy of body.
        const reqBody: any = {
            ...body,
            PickUpTime: body.PickUpTimeSlot,
            TelNo: body.TelNo,
            PickUpInstructions: getMultipleValuesAsText(puInstructions),
            CustomerInstructions: getMultipleValuesAsText(cInstructions),
        };

        // Order already created
        CreateCollectOrder(
            reqBody,
            (id: string) => {
                if (!id) {
                    return;
                }

                getQuote(
                    { orderId: id },
                    (res) => {
                        setOrderId(id);
                        setGetPriceLoading(false);
                    },
                    (err: any) => {
                        handleError(err);
                        handleFormChange();
                    }
                );
            },
            () => {
                handleFormChange();
                setOrkestroError(ORKESTRO_ERROR.Price);
            }
        );
    };

    const onExpire = () => {
        handleFormChange();
    };

    // Terms & Conditions Part
    const onTCSubmit = () => {
        setTCvisible(false);
        setTCAccepted(true);
    };
    const onTCCancel = () => {
        setTCvisible(false);
    };

    useDidMountEffect(() => {
        if (TCaccepted) {
            handlePlaceOrder();
        }
    }, [TCaccepted]);

    const changedModal = () => {
        return (
            <ConfirmModal
                visible={modalOpen}
                title={t('newCollectionOrder.orderPreferencesChanged')}
                buttonTitle={t('newCollectionOrder.getPrice')}
                errorBody={t('newCollectionOrder.orderPreferencesChangedBody')}
                onSubmit={() => {
                    onGetPrice();
                    setModalOpen(false);
                }}
                onClose={() => {
                    setModalOpen(false);
                }}
            />
        );
    };

    const vehicleTypeError = () => {
        return (
            <Box className={classes.quoteErrorBody}>
                <Typography>
                    {t('newCollectionOrder.sorryNoCourier')}
                </Typography>
                <Typography>
                    {t('newCollectionOrder.contactUs')}{" "}
                    <Link
                        href={`tel:${CONTACT_PHONE.replace(/ /g, "")}`}
                        style={{ color: theme.palette.primary.main }}
                    >
                        {CONTACT_PHONE}
                    </Link>{" "}
                    {t('newCollectionOrder.toDiscussOptions')}
                </Typography>
            </Box>
        );
    };

    const quoteErrorModal = () => {
        return (
            <ErrorModal
                visible={Boolean(quoteError)}
                title={t('newCollectionOrder.unableToFindKarter')}
                buttonTitle={t('common.ok')}
                customBody={() => vehicleTypeError()}
                onClose={() => {
                    setQuoteError("");
                }}
            />
        );
    };

    const genericErrorModal = () => {
        return (
            <ErrorModal
                visible={Boolean(orkestroError.length > 0)}
                title={t('newCollectionOrder.anErrorOccurred')}
                errorBody={orkestroError}
                onClose={() => {
                    handleFormChange();
                    setOrkestroError("");
                }}
            />
        );
    };

    const headerRight = () => {
        return (
            <Box
                className={classes.headerContainerNoMedia}
                sx={{ margin: "24px 24px 0 0" }}
            >
                <ButtonPrimary
                    disabled={
                        placeOrderLoading ||
                        hasAddressError ||
                        Boolean(flexibleCond) ||
                        !maxPriceQuoteId // Require max price for regular quotes
                    }
                    onClick={handleSubmit(() => handlePlaceOrder())}
                    title={t('newCollectionOrder.bookDelivery')}
                    noSetWidthAndHeight
                />
            </Box>
        );
    };

    const renderBasicInfo = () => {
        return (
            <InfoSectionContainer title={t('newCollectionOrder.deliveryDetails')}>
                {!isTkPortal && (
                    <Grid xs={12} item>
                        <CollectionOrderInput
                            name="MerchantName"
                            label={t('newCollectionOrder.collectionName')}
                            rules={{ required: DEFAULT_VALIDATION_MSG }}
                            control={control}
                            defaultValue=""
                        />
                    </Grid>
                )}
                <Grid xs={12} item>
                    <CollectionOrderCounterInput
                        label={t('newCollectionOrder.referenceNumber')}
                        name="ReferenceNumber"
                        rules={{
                            required: DEFAULT_VALIDATION_MSG,
                            maxLength: {
                                value: REF_NUMBER_LENGTH,
                                message: t('newCollectionOrder.referenceNumberShort')
                            }
                        }}
                        control={control}
                        value={body.ReferenceNumber}
                        defaultValue=""
                        showCounter
                        maxLength={REF_NUMBER_LENGTH}
                        inputProps={{
                            maxLength: REF_NUMBER_LENGTH
                        }}
                    />
                </Grid>
                <Grid xs={12} item>
                    <CollectionOrderCounterInput
                      multiline
                      name="OrderDetails"
                      label={t('newCollectionOrder.orderDetails')}
                      helperText={t('newCollectionOrder.orderDetailsHelper')}
                      rules={{
                        maxLength: {
                          value: 280,
                          message: t('newCollectionOrder.orderDetailsShort'),
                        }
                      }}
                      control={control}
                      value={body.OrderDetails}
                      defaultValue=""
                    />
                </Grid>
                <Grid xs={12} item>
                    <Box sx={{ marginTop: "16px" }}>
                        <FormControlLabel
                            control={
                                <Controller
                                    name={"HasRestrictedItems"}
                                    control={control}
                                    render={({ field: props }) => (
                                        <Checkbox
                                            {...props}
                                            color="primary"
                                            checked={props.value}
                                            onChange={(e) =>
                                                props.onChange(e.target.checked)
                                            }
                                        />
                                    )}
                                />
                            }
                            label={t('newCollectionOrder.restrictedItems')}
                        />
                    </Box>
                </Grid>
                <Grid xs={12} item>
                    <CollectionOrderCounterInput
                        multiline
                        name="PickUpInstructions"
                        label={t('newCollectionOrder.collectionInstructions')}
                        rules={{
                            maxLength: {
                                value: 100,
                                message: t('newCollectionOrder.collectionInstructionsShort'),
                            }
                        }}
                        control={control}
                        value={body.PickUpInstructions}
                        defaultValue=""
                    />
                </Grid>
            </InfoSectionContainer>
        );
    };

    const renderCollectionInfo = () => {
        return (
            <>
                <InfoSectionContainer
                    title="Collection Contact Details"
                    sx={{ mt: 3 }}
                    helperText="Update this section if you are not the designated person to handover the materials."
                >
                    <Grid
                        xs={12}
                        item
                        container
                        justifyContent="space-between"
                        direction="row"
                    >
                        <Grid xs={6} item style={{ paddingRight: "8px" }}>
                            <CollectionOrderInput
                                name="PickupFirstName"
                                label="First Name"
                                rules={{
                                    required: DEFAULT_VALIDATION_MSG,
                                }}
                                control={control}
                                placeholder="First Name"
                                defaultValue=""
                                fullWidth
                            />
                        </Grid>
                        <Grid xs={6} item style={{ paddingLeft: "8px" }}>
                            <CollectionOrderInput
                                name="PickupLastName"
                                label="Last Name"
                                rules={{
                                    required: DEFAULT_VALIDATION_MSG,
                                }}
                                control={control}
                                placeholder="Last Name"
                                defaultValue=""
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <CollectionOrderInput
                                name="PickupPhone"
                                label="Collection Phone Number"
                                rules={{
                                    required: DEFAULT_VALIDATION_MSG
                                }}
                                control={control}
                                placeholder="Collection Phone Number"
                                defaultValue=""
                                onBlur={(e) => {
                                    validatePhoneNumberField(e.target.value, 'pickup');
                                }}
                                error={Boolean(pickupPhoneError)}
                                helperText={pickupPhoneError}
                            />
                        </Grid>
                    </Grid>
                </InfoSectionContainer>
                <InfoSectionContainer
                    title="Collection address"
                    sx={{ mt: 3 }}
                    helperText="Start typing your address and select an option from suggested addresses."
                >
                    <Box mt={2}>
                        <GooglePlaceInput
                            label="Enter collection address"
                            placeholder="Collection address"
                            onPlaceChange={onCollectionAddressChange}
                            hasError={collectionAddressError}
                            _defaultValue={body.CollectionAddress}
                        />
                    </Box>
                </InfoSectionContainer>
            </>
        );
    };

    const disableDate = (date: any) => {
        let disable = true;
        if (!collectionTimeLoading) {
            const result = collectionTimeData.find((item) =>
                moment(date).isSame(moment(item.Start), "day")
            );
            if (result !== undefined) {
                disable = false;
            }
        }
        return disable;
    };

    const getMaxDate = () => {
        let maxDate = moment().format();
        if (collectionTimeData.length > 0) {
            collectionTimeData.forEach((item) => {
                const result = moment(item.Start).diff(moment(maxDate), "days");
                if (result) {
                    maxDate = moment(item.Start).format();
                }
            });
        }
        return maxDate;
    };

    const renderCollectionTime = () => {
        return (
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={enLocale}>
                <InfoSectionContainer
                    title={t('newCollectionOrder.collectionTime')}
                    helperText={t('newCollectionOrder.collectionTimeHelper')}
                    sx={{ mt: 3 }}
                >
                    <Controller
                        name="PickUpTime"
                        control={control}
                        rules={{ required: DEFAULT_VALIDATION_MSG }}
                        render={({ field: { ref, ...rest } }) => (
                            <KeyboardDatePickerInput
                                disablePast
                                label="Collection Date"
                                id="delivery-date"
                                format={getKeyboardPickerDateFormat()}
                                maxDate={getMaxDate()}
                                shouldDisableDate={disableDate}
                                initialFocusedDate={Date()}
                                invalidDateMessage="End date is required"
                                InputProps={{
                                    readOnly: true,
                                }}
                                {...rest}
                            />
                        )}
                    />

                    {pickupOptions &&
                        pickupOptions?.availableSlots?.length > 0 && (
                            <FormControl
                                data-testid="delivery-timeslot"
                                variant="outlined"
                                style={{ marginTop: 8 }}
                            >
                                <InputLabel id="delivery-timeslot-label">
                                    Collection from
                                </InputLabel>
                                <Controller
                                    name="PickUpTimeSlot"
                                    control={control}
                                    rules={{ required: DEFAULT_VALIDATION_MSG }}
                                    render={({
                                        field: { onChange, value },
                                    }) => (
                                        <Select
                                            labelId="delivery-timeslot"
                                            label="Collection from"
                                            value={value || ""}
                                            onChange={(event) => {
                                                onChange(event);
                                            }}
                                            disabled={Boolean(
                                                errors.PickUpTime
                                            )}
                                        >
                                            {pickupOptions?.availableSlots.map(
                                                (slot: any) => (
                                                    <MenuItem
                                                        key={slot.value}
                                                        value={slot.value}
                                                    >
                                                        {slot.label}
                                                    </MenuItem>
                                                )
                                            )}
                                        </Select>
                                    )}
                                />
                            </FormControl>
                        )}
                </InfoSectionContainer>
            </MuiPickersUtilsProvider>
        );
    };

    const renderVehicleType = () => {
        return (
            <InfoSectionContainer
                title="Vehicle type"
                helperText="Select the vehicle type appropriate for this delivery."
                sx={{ mt: 3 }}
            >
                <Controller
                    name="VehicleTypeId"
                    control={control}
                    rules={{ required: "Please select a vehicle type." }}
                    render={({ field: { ref, ...rest } }) => (
                        <>
                            <VehicleTypeSelector
                                style={{
                                    marginTop: 12,
                                    marginBottom: errors.VehicleTypeId ? 12 : 0,
                                }}
                                hasError={Boolean(errors.VehicleTypeId)}
                                {...rest}
                            />
                            {errors.VehicleTypeId && (
                                <Typography variant="caption" color="error">
                                    {errors.VehicleTypeId.message}
                                </Typography>
                            )}
                        </>
                    )}
                />
            </InfoSectionContainer>
        );
    };

    const renderDeliveryType = () => {
        return (
            <InfoSectionContainer
                title="Delivery type"
                helperText="Select the delivery option based on when you require your materials to be delivered."
                sx={{ mt: 3 }}
            >
                <Controller
                    name="DeliveryTypeKey"
                    control={control}
                    rules={{ required: "Please select a delivery type." }}
                    render={({ field: { ref, ...rest } }) => (
                        <>
                            <DeliveryTypeSelector
                                style={{
                                    marginTop: 12,
                                    marginBottom: errors.VehicleTypeId ? 12 : 0,
                                }}
                                hasError={Boolean(errors.VehicleTypeId)}
                                {...rest}
                            />
                            {errors.VehicleTypeId && (
                                <Typography
                                    variant="caption"
                                    color="error"
                                    style={{
                                        fontWeight: 400,
                                        letterSpacing: 0.4,
                                    }}
                                >
                                    {errors.VehicleTypeId.message as any}
                                </Typography>
                            )}
                        </>
                    )}
                />
            </InfoSectionContainer>
        );
    };

    const renderCustomerInformation = () => {
        return (
            <InfoSectionContainer
                title="Delivery Contact Details"
                sx={{ mt: 3 }}
            >
                <Grid
                    xs={12}
                    item
                    container
                    justifyContent="space-between"
                    direction="row"
                >
                    <Grid xs={6} item style={{ paddingRight: "8px" }}>
                        <CollectionOrderInput
                            name="FirstName"
                            label="First Name"
                            rules={{
                                required: DEFAULT_VALIDATION_MSG,
                            }}
                            control={control}
                            fullWidth
                        />
                    </Grid>
                    <Grid xs={6} item style={{ paddingLeft: "8px" }}>
                        <CollectionOrderInput
                            name="LastName"
                            label="Last Name"
                            rules={{
                                required: DEFAULT_VALIDATION_MSG,
                            }}
                            control={control}
                        />
                    </Grid>
                    <Grid xs={12} item>
                        <CollectionOrderInput
                            name="TelNo"
                            label="Phone Number"
                            rules={{
                                required: DEFAULT_VALIDATION_MSG,
                            }}
                            control={control}
                            onBlur={(e) => {
                                validatePhoneNumberField(e.target.value, 'delivery');
                            }}
                            error={Boolean(deliveryPhoneError)}
                            helperText={deliveryPhoneError}
                        />
                    </Grid>
                    <Grid xs={12} item>
                        <CollectionOrderInput
                            name="Email"
                            label="Email"
                            control={control}
                            rules={{
                                required: DEFAULT_VALIDATION_MSG,
                                pattern: EMAIL_VALIDATION,
                            }}
                        />
                    </Grid>
                    <Grid xs={12} item>
                        <CollectionOrderCounterInput
                            multiline
                            name="CustomerInstructions"
                            label="Delivery Instructions (Optional)"
                            rules={{
                                maxLength: {
                                    value: 65,
                                    message: "Please enter shorter instructions.",
                                }
                            }}
                            control={control}
                            value={body.CustomerInstructions}
                            defaultValue=""
                        />
                    </Grid>
                </Grid>
            </InfoSectionContainer>
        );
    };

    const renderDeliveryAddress = () => {
        return (
            <InfoSectionContainer
                title="Delivery address"
                sx={{ mt: 3 }}
                helperText="Start typing your address and select an option from suggested addresses."
            >
                <Box mt={2}>
                    <GooglePlaceInput
                        label="Enter delivery address"
                        placeholder="Delivery address"
                        onPlaceChange={onDeliveryAddressChange}
                        hasError={deliveryAddressError}
                        _defaultValue={body.DeliveryAddress}
                    />
                </Box>
            </InfoSectionContainer>
        );
    };

    const renderForm = () => {
        return (
            <form style={{ width: "100%", marginTop: 24, marginBottom: 72 }}>
                {renderBasicInfo()}
                {renderCollectionInfo()}
                {renderCollectionTime()}
                {renderVehicleType()}
                {renderDeliveryType()}
                {renderCustomerInformation()}
                {renderDeliveryAddress()}

                {flexibleCond && (
                    <Box
                        className={classes.headerContainerNoMedia}
                        sx={{ margin: "24px 24px 0 0" }}
                    >
                        {flexibleCond && (
                            <Typography color="error">
                                {flexibleError}
                            </Typography>
                        )}
                    </Box>
                )}

                <NewCollectionOrderTotal
                    lastUpdate={lastUpdate}
                    placeOrderLoading={placeOrderLoading}
                    getPriceLoading={getPriceLoading}
                    onGetPrice={handleSubmit(() => onGetPrice())}
                    orderId={orderId}
                    onExpire={onExpire}
                    hasError={hasAddressError || flexibleCond}
                    maxPriceQuoteId={maxPriceQuoteId}
                    setMaxPriceQuoteId={setMaxPriceQuoteId}
                    onCourierChange={(e: any) => {
                        setQuoteIds(e.ids);
                        setMaxPriceQuoteId(e.maxPriceQuoteId);
                        if (e.isPremium) {
                            // @ts-ignore
                            handleSubmit(onGetPrice());
                        }
                    }}
                    onSubmit={(id: string) => {
                        setQuoteIds([id] as never);
                        handlePlaceOrder(id);
                    }}
                />
                {headerRight()}
            </form>
        );
    };

    return (
        <Container className={classes.root}>
            <CollectionOrderHeader title="Add New Delivery" />
            {detailRequestLoading ? (
                <CircularProggressIndicator />
            ) : (
                renderForm()
            )}

            {changedModal()}
            {quoteErrorModal()}
            {genericErrorModal()}

            <AcceptTermsModal
                visible={TCvisible}
                onSubmit={onTCSubmit}
                onClose={onTCCancel}
            />
        </Container>
    );
}

export default NewCollectionOrder;
